<template>
  <div class="landing-wrapper">
    <v-container class="py-8 py-md-12" fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-alert v-if="error" type="error" class="mb-4" closable @click:close="error = ''">
            {{ error }}
          </v-alert>

          <div v-if="loading" class="d-flex justify-center py-10">
            <v-progress-circular indeterminate color="primary" size="48" />
          </div>

          <div v-else class="dv-panel">
            <div class="dv-grid">
              <!-- Columna izquierda: metadatos -->
              <div class="dv-info">
                <h1 class="dv-title">{{ doc.title }}</h1>

                <div class="dv-field">
                  <span class="dv-label">OBRA</span>
                  <span class="dv-value">{{ playTitle }}</span>
                </div>
                <div class="dv-field">
                  <span class="dv-label">ESTRENO DE LA FUNCIÓN</span>
                  <span class="dv-value">{{ formatDate(functionDate) }}</span>
                </div>
                <div class="dv-field">
                  <span class="dv-label">FECHA DE PAGO DEL 20%</span>
                  <span class="dv-value">{{ formatDate(reservation.depositPaidAt) }}</span>
                </div>
                <div class="dv-field">
                  <span class="dv-label">FECHA DE PAGO DEL 80%</span>
                  <span class="dv-value">{{ formatDate(reservation.balancePaidAt) }}</span>
                </div>

                <div class="dv-badge" :class="hasFile ? 'dv-badge-ok' : 'dv-badge-pending'">
                  {{ hasFile ? 'DOCUMENTO DISPONIBLE' : doc.pendingLabel }}
                </div>

                <div class="dv-field dv-field-paidto">
                  <span class="dv-label">PAGADO A</span>
                  <span class="dv-value dv-value-strong">{{ paidTo }}</span>
                </div>

                <!-- Desglose del valor (mockup "27 pagos") -->
                <div class="dv-amounts">
                  <div class="dv-field">
                    <span class="dv-label">VALOR BASE (OBRA)</span>
                    <span class="dv-value dv-value-strong">{{ formatCLP(reservation.totalAmount) }}</span>
                  </div>
                  <div class="dv-field">
                    <span class="dv-label">20% (RESERVA)</span>
                    <span class="dv-value dv-value-strong">{{ formatCLP(reservation.depositAmount) }}</span>
                  </div>
                  <div class="dv-field">
                    <span class="dv-label">80% (SALDO)</span>
                    <span class="dv-value dv-value-strong">{{ formatCLP(reservation.balanceAmount) }}</span>
                  </div>
                </div>

                <button type="button" class="dv-back" @click="goBack">
                  <v-icon size="16">mdi-arrow-left</v-icon>
                  <span>VOLVER</span>
                </button>
              </div>

              <!-- Columna derecha: vista previa + acciones -->
              <div class="dv-preview-col">
                <div class="dv-preview">
                  <img
                    v-if="hasFile && isImage(fileUrl)"
                    :src="fileUrl"
                    class="dv-preview-img"
                    alt="Vista previa del documento"
                  />
                  <iframe
                    v-else-if="hasFile && isPdf(fileUrl)"
                    :src="fileUrl"
                    class="dv-preview-frame"
                    title="Vista previa del documento"
                  ></iframe>
                  <div v-else class="dv-preview-empty">
                    <v-icon size="64" color="rgba(255,255,255,0.35)">
                      {{ hasFile ? 'mdi-file-document-outline' : 'mdi-file-hidden' }}
                    </v-icon>
                    <span>{{ hasFile ? 'Vista previa no disponible' : 'Documento aún no disponible' }}</span>
                  </div>
                </div>

                <div class="dv-actions">
                  <a
                    class="dv-btn dv-btn-view"
                    :class="{ 'dv-btn-off': !hasFile }"
                    :href="hasFile ? fileUrl : undefined"
                    target="_blank"
                    rel="noopener"
                  >VER</a>
                  <a
                    class="dv-btn dv-btn-download"
                    :class="{ 'dv-btn-off': !hasFile }"
                    :href="hasFile ? fileUrl : undefined"
                    :download="hasFile ? '' : undefined"
                    target="_blank"
                    rel="noopener"
                  >DESCARGAR</a>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <teatro-footer />
  </div>
</template>

<script>
import { fetchReservation } from "@/services/teatroApi";

// Configuración de cada tipo de documento de una reserva.
const DOC_TYPES = {
  contract: {
    title: "CONTRATO",
    urlKey: "contractFileUrl",
    pendingLabel: "CONTRATO PENDIENTE",
  },
  reservation_invoice: {
    title: "FACTURA POR EL PAGO 20% (RESERVA)",
    urlKey: "reservationInvoiceUrl",
    pendingLabel: "FACTURA PENDIENTE",
  },
  deposit_proof: {
    title: "COMPROBANTE DE PAGO 20% (RESERVA)",
    urlKey: "depositProofUrl",
    pendingLabel: "COMPROBANTE PENDIENTE",
  },
  balance_invoice: {
    title: "FACTURA POR EL PAGO 80% (SALDO)",
    urlKey: "balanceInvoiceUrl",
    pendingLabel: "FACTURA PENDIENTE",
  },
  balance_proof: {
    title: "COMPROBANTE DE PAGO 80% (SALDO)",
    urlKey: "balanceProofUrl",
    pendingLabel: "COMPROBANTE PENDIENTE",
  },
  // Tramo administración <-> compañía de teatro.
  company_advance_proof: {
    title: "COMPROBANTE DEL ANTICIPO (10%) A LA COMPAÑÍA DE TEATRO",
    urlKey: "companyAdvanceProofUrl",
    pendingLabel: "COMPROBANTE PENDIENTE",
  },
  company_advance_invoice: {
    title: "FACTURA DE LA COMPAÑÍA POR EL ANTICIPO (10%)",
    urlKey: "companyAdvanceInvoiceUrl",
    pendingLabel: "FACTURA PENDIENTE",
  },
  company_payment_proof: {
    title: "COMPROBANTE DEL SALDO (80%) A LA COMPAÑÍA DE TEATRO",
    urlKey: "companyPaymentProofUrl",
    pendingLabel: "COMPROBANTE PENDIENTE",
  },
  company_invoice: {
    title: "FACTURA DE LA COMPAÑÍA POR EL SALDO (80%)",
    urlKey: "companyInvoiceUrl",
    pendingLabel: "FACTURA PENDIENTE",
  },
};

// Orden de preferencia al auto-seleccionar un documento (más avanzado primero).
const AUTO_ORDER = [
  "company_invoice",
  "company_payment_proof",
  "company_advance_invoice",
  "company_advance_proof",
  "balance_proof",
  "balance_invoice",
  "deposit_proof",
  "reservation_invoice",
  "contract",
];

export default {
  name: "DocumentViewer",
  props: {
    reservationId: { type: [Number, String], default: null },
    // Tipo de documento; "auto" elige el más relevante disponible.
    docType: { type: String, default: "auto" },
  },
  data() {
    return {
      reservation: null,
      resolvedType: null,
      loading: true,
      error: "",
    };
  },
  computed: {
    doc() {
      return DOC_TYPES[this.resolvedType] || DOC_TYPES.reservation_invoice;
    },
    fileUrl() {
      return (this.reservation && this.reservation[this.doc.urlKey]) || "";
    },
    hasFile() {
      return !!this.fileUrl;
    },
    playTitle() {
      return (this.reservation && this.reservation.play && this.reservation.play.title) || "—";
    },
    paidTo() {
      const company = this.reservation && this.reservation.play && this.reservation.play.company;
      return (company && company.name) || "SOMOS TEATRO";
    },
    functionDate() {
      const r = this.reservation;
      return r ? r.confirmedDate || r.displayDate || r.requestedDate : null;
    },
  },
  async created() {
    this.loading = true;
    this.error = "";
    try {
      this.reservation = await fetchReservation(this.reservationId);
      if (!this.reservation) {
        this.error = "No se encontró el documento solicitado.";
        return;
      }
      this.resolvedType = this.resolveType();
    } catch (e) {
      this.error = "No se pudo cargar el documento.";
      console.error(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    resolveType() {
      if (this.docType && this.docType !== "auto" && DOC_TYPES[this.docType]) {
        return this.docType;
      }
      // Auto: primer documento con archivo, según el orden de preferencia.
      for (const key of AUTO_ORDER) {
        if (this.reservation[DOC_TYPES[key].urlKey]) return key;
      }
      return "reservation_invoice";
    },
    formatDate(iso) {
      if (!iso) return "—";
      const [y, m, d] = String(iso).slice(0, 10).split("-");
      return y && m && d ? `${d}/${m}/${y}` : "—";
    },
    formatCLP(amount) {
      const value = Number(amount) || 0;
      return "$ " + value.toLocaleString("es-CL");
    },
    isPdf(url) {
      return /\.pdf(\?|$)/i.test(String(url || ""));
    },
    isImage(url) {
      return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(String(url || ""));
    },
    goBack() {
      if (window.history.length > 1) window.history.back();
      else window.location.href = "/historial/";
    },
  },
};
</script>

<style scoped>
.dv-panel {
  max-width: 1080px;
  margin: 0 auto;
  background: rgba(8, 5, 5, 0.66);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 26px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
  padding: 44px 48px;
}

.dv-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
}

/* --- Columna izquierda --- */
.dv-info {
  display: flex;
  flex-direction: column;
}

.dv-title {
  font-family: var(--font-family-heading);
  font-size: 26px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--color-theater-text);
  margin: 0 0 26px;
}

.dv-field {
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.dv-label {
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.9px;
  color: rgba(255, 255, 255, 0.6);
}

.dv-value {
  font-family: var(--font-family-heading);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: var(--color-theater-text);
}

.dv-value-strong {
  text-transform: uppercase;
}

.dv-badge {
  align-self: flex-start;
  padding: 8px 16px;
  border-radius: 8px;
  font-family: var(--font-family-heading);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  margin: 6px 0 22px;
}

.dv-badge-pending {
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: rgba(255, 255, 255, 0.85);
}

.dv-badge-ok {
  border: 1px solid #46c98b;
  color: #46c98b;
}

.dv-field-paidto {
  margin-bottom: 26px;
}

/* Desglose valor base / 20% / 80% */
.dv-amounts {
  padding-top: 20px;
  margin-bottom: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.dv-back {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: none;
  color: var(--color-theater-text);
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
  padding: 0;
  opacity: 0.85;
  transition: opacity 0.15s ease;
}

.dv-back:hover {
  opacity: 1;
}

/* --- Columna derecha --- */
.dv-preview-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
}

.dv-preview {
  width: 100%;
  max-width: 380px;
  aspect-ratio: 3 / 4;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dv-preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff;
}

.dv-preview-frame {
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
}

.dv-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: rgba(0, 0, 0, 0.45);
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-align: center;
  padding: 20px;
}

.dv-actions {
  display: flex;
  gap: 14px;
  width: 100%;
  max-width: 380px;
  justify-content: center;
}

.dv-btn {
  flex: 1;
  text-align: center;
  padding: 13px 18px;
  border-radius: 8px;
  text-decoration: none;
  font-family: var(--font-family-heading);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.dv-btn-view {
  background: #3a3a3a;
  color: #fff;
}

.dv-btn-view:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.45);
}

.dv-btn-download {
  background: linear-gradient(90deg, #c8102e 0%, #a50d25 100%);
  color: #fff;
}

.dv-btn-download:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(200, 16, 46, 0.45);
}

.dv-btn-off {
  opacity: 0.4;
  pointer-events: none;
}

@media (max-width: 860px) {
  .dv-panel {
    padding: 30px 24px;
  }

  .dv-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .dv-preview-col {
    order: -1;
  }
}
</style>
